import React, { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase/config';
import { ReportDocument } from '@curbside-quest/shared';
import { SocialMediaCheckboxes } from './SocialMediaCheckboxes';
import { SocialMediaAuditLog } from './SocialMediaAuditLog';
import { SocialMediaConfigStatus } from './SocialMediaConfigStatus';

// Helper function to safely convert Firebase timestamps to Date objects
const safeToDate = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  
  // If it's a Firestore Timestamp with toDate method
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  // If it's a plain object with seconds (from Firebase Functions)
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  
  // If it's already a Date object
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  // If it's a number (milliseconds)
  if (typeof timestamp === 'number') {
    return new Date(timestamp);
  }
  
  // If it's a string
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  
  // Fallback to current date
  return new Date();
};

interface ModerationReport extends ReportDocument {
  aiAnalysis?: {
    suggestedTags: string[];
    generatedTitle: string;
    generatedDescription: string;
    contentFlags: Array<{
      type: string;
      severity: 'low' | 'medium' | 'high';
      details: string;
    }>;
    confidence: number;
  };
}

type ModerationStatusFilter = 'pending' | 'active' | 'going_soon' | 'gone' | 'rejected';

interface ApprovalProgress {
  reportId: string;
  stage: 'approving' | 'sharing' | 'completed' | 'failed';
  message: string;
  socialMediaResults?: {
    [platform: string]: {
      success: boolean;
      postId?: string;
      error?: string;
    };
  };
}

export const PendingReportsQueue: React.FC = () => {
  const [reports, setReports] = useState<ModerationReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<ModerationReport | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [markAsGoneLoading, setMarkAsGoneLoading] = useState<string | null>(null);
  const [approvalProgress, setApprovalProgress] = useState<ApprovalProgress | null>(null);
  const [lastActionResult, setLastActionResult] = useState<{
    success: boolean;
    message: string;
    type: 'approve' | 'reject' | 'markGone';
    socialMediaResults?: any;
  } | null>(null);
  const [statusFilter, setStatusFilter] = useState<ModerationStatusFilter>('pending');

  const statusOptions: Array<{ value: ModerationStatusFilter; label: string }> = [
    { value: 'pending', label: 'Pending Review' },
    { value: 'active', label: 'Active' },
    { value: 'going_soon', label: 'Going Soon' },
    { value: 'gone', label: 'Gone' },
    { value: 'rejected', label: 'Rejected' }
  ];

  // Memoize Firebase Functions - same pattern as useReports
  const getPendingReportsFunction = useMemo(() => httpsCallable(functions, 'getPendingReports'), []);
  const approveReportFunction = useMemo(() => httpsCallable(functions, 'approveReport'), []);
  const rejectReportFunction = useMemo(() => httpsCallable(functions, 'rejectReport'), []);
  const updateReportStatusFunction = useMemo(() => httpsCallable(functions, 'updateReportStatus'), []);
  const debugGetAllReportsFunction = useMemo(() => httpsCallable(functions, 'debugGetAllReports'), []);
  const testAuthFunction = useMemo(() => httpsCallable(functions, 'testAuth'), []);

  useEffect(() => {
    loadReports(statusFilter);
  }, [statusFilter]);

  const loadReports = async (status: ModerationStatusFilter = statusFilter) => {
    try {
      setLoading(true);
      const result = await getPendingReportsFunction({ limit: 50, status });
      const data = result.data as { reports: ModerationReport[] };
      setReports(data.reports || []);
      if (selectedReport && !data.reports?.find(report => report.id === selectedReport.id)) {
        setSelectedReport(null);
      }
    } catch (error) {
      console.error('Error loading pending reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reportId: string, notes?: string, socialMediaPlatforms?: string[]) => {
    try {
      setActionLoading(reportId);
      setLastActionResult(null);
      
      // Set initial progress
      setApprovalProgress({
        reportId,
        stage: 'approving',
        message: 'Approving report...'
      });

      const result = await approveReportFunction({ 
        reportId, 
        approvalNotes: notes || '',
        socialMediaPlatforms: socialMediaPlatforms || ['facebook', 'twitter', 'instagram']
      });
      
      // Update progress for social media sharing
      if (socialMediaPlatforms && socialMediaPlatforms.length > 0) {
        setApprovalProgress({
          reportId,
          stage: 'sharing',
          message: `Sharing on ${socialMediaPlatforms.length} social media platform${socialMediaPlatforms.length > 1 ? 's' : ''}...`
        });

        // Wait a moment to simulate the social media posting process
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      // Mark as completed
      setApprovalProgress({
        reportId,
        stage: 'completed',
        message: 'Report approved successfully!'
      });

      // Set success result
      setLastActionResult({
        success: true,
        message: `Report approved successfully${socialMediaPlatforms && socialMediaPlatforms.length > 0 ? ' and shared on social media' : ''}!`,
        type: 'approve',
        socialMediaResults: (result.data as any)?.socialMediaResults
      });
      
      // Refresh list based on current filter
      if (statusFilter === 'pending') {
        setReports(prev => prev.filter(r => r.id !== reportId));
        setSelectedReport(null);
      } else {
        await loadReports(statusFilter);
      }
      
      console.log('Report approved:', reportId, 'with social media platforms:', socialMediaPlatforms);
      
      // Clear progress after a delay
      setTimeout(() => {
        setApprovalProgress(null);
        setLastActionResult(null);
      }, 5000);
      
    } catch (error) {
      console.error('Error approving report:', error);
      
      setApprovalProgress({
        reportId,
        stage: 'failed',
        message: 'Failed to approve report'
      });

      setLastActionResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to approve report. Please try again.',
        type: 'approve'
      });

      // Clear error after a delay
      setTimeout(() => {
        setApprovalProgress(null);
        setLastActionResult(null);
      }, 5000);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (reportId: string, reason: string, notes?: string) => {
    try {
      setActionLoading(reportId);
      await rejectReportFunction({
        reportId,
        rejectionReason: reason,
        rejectionNotes: notes || ''
      });

      // Remove from pending list
      setReports(prev => prev.filter(r => r.id !== reportId));
      setSelectedReport(null);

      console.log('Report rejected:', reportId, reason);
      setLastActionResult({ success: true, message: 'Report rejected successfully.', type: 'reject' });
      setTimeout(() => setLastActionResult(null), 4000);
    } catch (error) {
      console.error('Error rejecting report:', error);
      setLastActionResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to reject report. Please try again.',
        type: 'reject'
      });
      setTimeout(() => setLastActionResult(null), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkAsGone = async (reportId: string) => {
    try {
      setMarkAsGoneLoading(reportId);
      await updateReportStatusFunction({ reportId, status: 'gone' });

      if (statusFilter === 'gone') {
        await loadReports(statusFilter);
      } else {
        setReports(prev => prev.filter(r => r.id !== reportId));
        setSelectedReport(null);
      }

      setLastActionResult({ success: true, message: 'Report marked as gone.', type: 'markGone' });
      setTimeout(() => setLastActionResult(null), 4000);
    } catch (error) {
      console.error('Error marking report as gone:', error);
      setLastActionResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to mark report as gone. Please try again.',
        type: 'markGone'
      });
      setTimeout(() => setLastActionResult(null), 5000);
    } finally {
      setMarkAsGoneLoading(null);
    }
  };

  const activeFilterLabel = statusOptions.find(option => option.value === statusFilter)?.label || 'Reports';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const hasReports = reports.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Reports ({reports.length})</h2>
          <p className="text-sm text-gray-500">Showing {activeFilterLabel.toLowerCase()} reports.</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              value={statusFilter}
              onChange={event => setStatusFilter(event.target.value as ModerationStatusFilter)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={async () => {
                try {
                  const result = await testAuthFunction();
                  console.log('Auth test result:', result.data);
                  const data = result.data as { message: string };
                  alert(`Auth test: ${data.message}`);
                } catch (error) {
                  console.error('Auth test error:', error);
                  const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                  alert(`Auth test failed: ${errorMessage}`);
                }
              }}
              className="text-green-600 hover:text-green-800 text-sm font-medium"
            >
              Test Auth
            </button>
            <button
              onClick={async () => {
                try {
                  const result = await debugGetAllReportsFunction();
                  console.log('All reports debug:', result.data);
                  alert('Check console for all reports data');
                } catch (error) {
                  console.error('Debug error:', error);
                }
              }}
              className="text-purple-600 hover:text-purple-800 text-sm font-medium"
            >
              Debug All Reports
            </button>
            <button
              onClick={() => loadReports(statusFilter)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      {approvalProgress && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {approvalProgress.stage === 'completed' ? (
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : approvalProgress.stage === 'failed' ? (
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              ) : (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{approvalProgress.message}</p>
              {approvalProgress.stage === 'sharing' && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success/Failure Feedback */}
      {lastActionResult && (
        <div className={`border rounded-lg p-4 mb-6 ${
          lastActionResult.success
            ? 'bg-green-50 border-green-200'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              {lastActionResult.success ? (
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                lastActionResult.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {lastActionResult.message}
              </p>
              {lastActionResult.type === 'approve' && lastActionResult.socialMediaResults && (
                <div className="mt-2 space-y-1">
                  {Object.entries(lastActionResult.socialMediaResults).map(([platform, result]: [string, any]) => (
                    <div key={platform} className="flex items-center space-x-2 text-xs">
                      <span className="capitalize font-medium">{platform}:</span>
                      {result?.success ? (
                        <span className="text-green-600">✓ Posted successfully</span>
                      ) : (
                        <span className="text-red-600">✗ {result?.error || 'Failed to post'}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Reports List */}
        <div className="space-y-4">
          {hasReports ? (
            reports.map(report => (
              <div
                key={report.id}
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  selectedReport?.id === report.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedReport(report)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 mb-1">Report from {report.location?.suburb || 'Unknown'}</h3>
                    {/* Description removed - showing tags instead */}
                    {report.tags.length > 0 && (
                      <p className="text-sm text-gray-600 mb-2">Tags: {report.tags.join(', ')}</p>
                    )}
                    <div className="flex items-center text-xs text-gray-500 space-x-4">
                      <span>{report.location?.suburb || 'Unknown'}</span>
                      <span>{safeToDate(report.submittedAt).toLocaleDateString()}</span>
                      {report.aiAnalysis && (
                        <span className="text-green-600">
                          AI: {Math.round(report.aiAnalysis.confidence * 100)}%
                        </span>
                      )}
                    </div>

                    {/* Content Flags */}
                    {report.aiAnalysis?.contentFlags && report.aiAnalysis.contentFlags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {report.aiAnalysis.contentFlags.map((flag, index) => (
                          <span
                            key={index}
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              flag.severity === 'high'
                                ? 'bg-red-100 text-red-800'
                                : flag.severity === 'medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            ⚠️ {flag.type}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {report.images.length > 0 && (
                    <img
                      src={report.images[0].url}
                      alt="Report"
                      className="w-16 h-16 object-cover rounded ml-4"
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-lg mb-2">📭</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No {activeFilterLabel.toLowerCase()} reports</h3>
              <p className="text-gray-500">Try selecting a different status or check back later.</p>
            </div>
          )}
        </div>

        {/* Report Details */}
        {selectedReport && (
          <div className="border rounded-lg p-6 bg-white sticky top-4">
            <ReportReviewPanel
              report={selectedReport}
              onApprove={handleApprove}
              onReject={handleReject}
              onMarkAsGone={handleMarkAsGone}
              loading={actionLoading === selectedReport.id}
              markAsGoneLoading={markAsGoneLoading === selectedReport.id}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface ReportReviewPanelProps {
  report: ModerationReport;
  onApprove: (reportId: string, notes?: string, socialMediaPlatforms?: string[]) => void;
  onReject: (reportId: string, reason: string, notes?: string) => void;
  onMarkAsGone: (reportId: string) => void;
  loading: boolean;
  markAsGoneLoading: boolean;
}

const ReportReviewPanel: React.FC<ReportReviewPanelProps> = ({
  report,
  onApprove,
  onReject,
  onMarkAsGone,
  loading,
  markAsGoneLoading
}) => {
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedSocialMediaPlatforms, setSelectedSocialMediaPlatforms] = useState<string[]>([
    'facebook', 'twitter', 'instagram'
  ]);
  const [activeTab, setActiveTab] = useState<'review' | 'audit' | 'config'>('review');

  const handleSubmit = () => {
    if (action === 'approve') {
      onApprove(report.id, notes, selectedSocialMediaPlatforms);
    } else if (action === 'reject' && rejectionReason) {
      onReject(report.id, rejectionReason, notes);
    }
    setAction(null);
    setNotes('');
    setRejectionReason('');
    setSelectedSocialMediaPlatforms(['facebook', 'twitter', 'instagram']);
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('review')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'review'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Review Report
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'audit'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Sharing History
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'config'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Configuration
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'review' && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Report from {report.location?.suburb || 'Unknown'}</h3>
        {/* Description removed - showing tags and location details instead */}
        <div className="text-gray-600 mb-4">
          <p><strong>Location:</strong> {report.location?.address || 'Unknown'}</p>
          {report.tags.length > 0 && (
            <p><strong>Tags:</strong> {report.tags.join(', ')}</p>
          )}
        </div>
        
        {/* Images */}
        {report.images.length > 0 && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            {report.images.map((image, index) => (
              <img
                key={index}
                src={image.url}
                alt={`Report image ${index + 1}`}
                className="w-full h-32 object-cover rounded"
              />
            ))}
          </div>
        )}

        {/* Location */}
        <div className="text-sm text-gray-600 mb-4">
          <p><strong>Location:</strong> {report.location?.address || 'Unknown'}</p>
          <p><strong>Suburb:</strong> {report.location?.suburb || 'Unknown'}</p>
          <p><strong>Council:</strong> {report.location?.council || 'Unknown'}</p>
        </div>

        {/* Tags */}
        <div className="mb-4">
          <strong className="text-sm text-gray-700">Tags:</strong>
          <div className="flex flex-wrap gap-1 mt-1">
            {report.tags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* AI Analysis */}
        {report.aiAnalysis && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-gray-900 mb-2">AI Analysis</h4>
            <div className="space-y-2 text-sm">
              <p><strong>Confidence:</strong> {Math.round(report.aiAnalysis.confidence * 100)}%</p>
              <p><strong>Suggested Title:</strong> {report.aiAnalysis.generatedTitle}</p>
              <p><strong>Suggested Description:</strong> {report.aiAnalysis.generatedDescription}</p>
              <div>
                <strong>Suggested Tags:</strong>
                <div className="flex flex-wrap gap-1 mt-1">
                  {report.aiAnalysis.suggestedTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {!action && (
        <div className="space-y-3">
          <div className="flex space-x-3">
            <button
              onClick={() => setAction('approve')}
              disabled={loading}
              className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              Approve
            </button>
            <button
              onClick={() => setAction('reject')}
              disabled={loading}
              className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              Reject
            </button>
          </div>
          {(report.status === 'active' || report.status === 'going_soon') && (
            <button
              onClick={() => onMarkAsGone(report.id)}
              disabled={loading || markAsGoneLoading}
              className="w-full bg-gray-800 text-white px-4 py-2 rounded-md hover:bg-gray-900 disabled:opacity-50"
            >
              {markAsGoneLoading ? 'Marking as Gone...' : 'Mark as Gone'}
            </button>
          )}
        </div>
      )}

      {/* Action Form */}
      {action && (
        <div className="space-y-4">
          {action === 'reject' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rejection Reason *
              </label>
              <select
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              >
                <option value="">Select a reason</option>
                <option value="inappropriate_content">Inappropriate Content</option>
                <option value="spam">Spam</option>
                <option value="duplicate">Duplicate Report</option>
                <option value="invalid_location">Invalid Location</option>
                <option value="poor_quality">Poor Quality Images/Description</option>
                <option value="other">Other</option>
              </select>
            </div>
          )}

          {action === 'approve' && (
            <SocialMediaCheckboxes
              selectedPlatforms={selectedSocialMediaPlatforms}
              onSelectionChange={setSelectedSocialMediaPlatforms}
              disabled={loading}
            />
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {action === 'approve' ? 'Approval Notes' : 'Additional Notes'} (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder={`Add any ${action === 'approve' ? 'approval' : 'rejection'} notes...`}
            />
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleSubmit}
              disabled={loading || (action === 'reject' && !rejectionReason)}
              className={`flex-1 px-4 py-2 rounded-md text-white disabled:opacity-50 ${
                action === 'approve'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {loading ? 'Processing...' : `Confirm ${action === 'approve' ? 'Approval' : 'Rejection'}`}
            </button>
            <button
              onClick={() => {
                setAction(null);
                setNotes('');
                setRejectionReason('');
                setSelectedSocialMediaPlatforms(['facebook', 'twitter', 'instagram']);
              }}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
        </div>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'audit' && (
        <div>
          <SocialMediaAuditLog reportId={report.id} />
        </div>
      )}

      {/* Configuration Tab */}
      {activeTab === 'config' && (
        <div>
          <SocialMediaConfigStatus showDetails={true} />
        </div>
      )}
    </div>
  );
};
